import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isAdmin,
  getPendingUsers,
  getVerifiedUsers,
  verifyUser,
  rejectUser,
  getDaysRemaining,
  getUserStats,
  cleanupExpiredUsers
} from '../utils/userAuth';
import './AdminDashboard.css';

function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const communityNames = {
    indian: 'Indian Students',
    pakistani: 'Pakistani Students',
    brazilian: 'Brazilian Students',
    mexican: 'Mexican Students',
    african: 'African Students',
    chinese: 'Chinese Students',
    korean: 'Korean Students',
    filipino: 'Filipino Students',
    caribbean: 'Caribbean Students',
    european: 'European Students',
    arab: 'Arab Students',
    latinx: 'LatinX Students',
  };

  useEffect(() => {
    // Check if user is admin
    if (!user || !isAdmin(user.email)) {
      navigate('/');
      return;
    }

    loadData();
  }, [user, navigate]);

  const loadData = () => {
    // Clean up expired users first
    cleanupExpiredUsers();

    setPendingUsers(getPendingUsers());
    setVerifiedUsers(getVerifiedUsers());
    setStats(getUserStats());
  };

  const handleVerify = (userId) => {
    setActionLoading(userId);
    try {
      verifyUser(userId);
      loadData();
    } catch (err) {
      alert(err.message);
    }
    setActionLoading(null);
  };

  const handleReject = (userId, userName) => {
    if (window.confirm(`Are you sure you want to reject and remove ${userName}?`)) {
      setActionLoading(userId);
      try {
        rejectUser(userId);
        loadData();
      } catch (err) {
        alert(err.message);
      }
      setActionLoading(null);
    }
  };

  if (!user || !isAdmin(user.email)) {
    return null;
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back
        </button>
        <div className="header-title">
          <h1>Admin Dashboard</h1>
          <span>User Verification Portal</span>
        </div>
        <button className="refresh-btn" onClick={loadData}>
          Refresh
        </button>
      </header>

      {/* Stats */}
      {stats && (
        <section className="admin-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Users</span>
          </div>
          <div className="stat-card verified">
            <span className="stat-value">{stats.verified}</span>
            <span className="stat-label">Verified</span>
          </div>
          <div className="stat-card pending">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat-card warning">
            <span className="stat-value">{stats.expiringSoon}</span>
            <span className="stat-label">Expiring Soon</span>
          </div>
        </section>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending ({pendingUsers.length})
        </button>
        <button
          className={`tab ${activeTab === 'verified' ? 'active' : ''}`}
          onClick={() => setActiveTab('verified')}
        >
          Verified ({verifiedUsers.length})
        </button>
      </div>

      {/* User Lists */}
      <div className="user-list">
        {activeTab === 'pending' && (
          <>
            {pendingUsers.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">✓</span>
                <p>No pending verification requests</p>
              </div>
            ) : (
              pendingUsers.map(u => {
                const daysLeft = getDaysRemaining(u.expiresAt);
                return (
                  <div key={u.id} className="user-card">
                    <div className="user-info">
                      <div className="user-avatar">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-details">
                        <h3>{u.name}</h3>
                        <p className="user-email">{u.email}</p>
                        <div className="user-meta">
                          <span className="community-tag">
                            {communityNames[u.community] || u.community}
                          </span>
                          <span className="student-id">ID: {u.studentId}</span>
                        </div>
                        <p className="registered-date">
                          Registered: {new Date(u.registeredAt).toLocaleDateString()}
                        </p>
                        {daysLeft !== null && (
                          <p className={`expiry-warning ${daysLeft <= 2 ? 'urgent' : ''}`}>
                            {daysLeft === 0 ? 'Expires today!' : `${daysLeft} days remaining`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="user-actions">
                      <button
                        className="verify-btn"
                        onClick={() => handleVerify(u.id)}
                        disabled={actionLoading === u.id}
                      >
                        {actionLoading === u.id ? '...' : 'Verify'}
                      </button>
                      <button
                        className="reject-btn"
                        onClick={() => handleReject(u.id, u.name)}
                        disabled={actionLoading === u.id}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}

        {activeTab === 'verified' && (
          <>
            {verifiedUsers.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">🦚</span>
                <p>No verified users yet</p>
              </div>
            ) : (
              verifiedUsers.map(u => (
                <div key={u.id} className="user-card verified">
                  <div className="user-info">
                    <div className="user-avatar verified">
                      {u.name.charAt(0).toUpperCase()}
                      <span className="verified-badge">✓</span>
                    </div>
                    <div className="user-details">
                      <h3>
                        {u.name}
                        <span className="verified-tick">✓</span>
                      </h3>
                      <p className="user-email">{u.email}</p>
                      <div className="user-meta">
                        <span className="community-tag">
                          {communityNames[u.community] || u.community}
                        </span>
                        <span className="student-id">ID: {u.studentId}</span>
                      </div>
                      <p className="verified-date">
                        Verified: {new Date(u.verifiedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="user-actions">
                    <button
                      className="remove-btn"
                      onClick={() => handleReject(u.id, u.name)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
